import { computed, observable, runInAction } from "mobx"

import { withGlobalLoader } from "@/lib/context.ts"
import { getLogger } from "@/lib/logging.ts"
import { Model } from "@/lib/Model.ts"

import type {
  PR_Project2,
  ProjectSettings,
  TID,
  UPreviewProcessesApiState
} from "@/lib/client/generated/TypeDefinitions.ts"
import type { OrgContext } from "@/lib/context.ts"
import type { IObservableValue } from "mobx"

const log = getLogger("ProjectPooledPreviewViewModel")

export class ProjectPooledPreviewViewModel extends Model {
  readonly error = observable.box<string | null>(null)

  readonly settings = observable.box<ProjectSettings | null>(null)
  readonly pooledPreviewId = observable.box<TID | null>(null)
  readonly pooledPreviewState = observable.box<UPreviewProcessesApiState | null>(null)

  readonly isReadonly = computed(() => !this.canEditProject.get())

  constructor(
    readonly context: OrgContext,
    readonly project: PR_Project2,
    readonly canEditProject: IObservableValue<boolean>
  ) {
    super("ProjectPooledPreviewViewModel")

    void this.load()
  }

  get projectKey() {
    return this.project.key
  }

  get repository() {
    return this.project.repository.name
  }

  async load() {
    runInAction(() => {
      this.isLoading.set(true)
      this.error.set(null)
    })

    try {
      const [projectSettings, pooledPreviewId] = await withGlobalLoader(this.context, Promise.all([
        this.context.queries.getProjectSettings(this.projectKey),
        this.context.queries.getPooledPreview(this.projectKey, this.repository)
      ]))

      runInAction(() => {
        this.settings.set(projectSettings)
        this.pooledPreviewId.set(pooledPreviewId)
        this.isLoading.set(false)
      })

      // If there's a pooled preview, subscribe to its state
      if (pooledPreviewId) {
        void this.subscribeToPooledPreview(pooledPreviewId)
      }
    } catch (error) {
      log.error("Failed to load pooled preview settings:", error)
      runInAction(() => {
        this.error.set(`Failed to load pooled preview settings: ${error}`)
        this.isLoading.set(false)
      })
    }
  }

  async subscribeToPooledPreview(previewId: TID) {
    try {
      const observable = this.context.queries.subscribeToPreviewProcess(previewId, null)

      observable.subscribe({
        next: (state: UPreviewProcessesApiState) => {
          runInAction(() => {
            this.pooledPreviewState.set(state)
          })
        },
        error: (error: unknown) => {
          log.error("Failed to subscribe to pooled preview state:", error)
        }
      })
    } catch (error) {
      log.error("Failed to subscribe to pooled preview state:", error)
    }
  }

}
